import styled from 'styled-components';
import { cueColorCss, cueText, VisibleCue } from './learn_cue.ts';
import { shortScreen } from './learn_screen_parts.tsx';

// The Ready · Set · Go! row: Rhythm Echo's (issue #3519), where it takes the slack between the
// notation and the tap button (the slot the beat dots occupied, grown to fill the space), and Sing
// in Solfège's (issue #3722, round 2), where it takes the slack between the status line under the
// staff and the meter on the sing screen. It began in Rhythm Echo and moved here, unchanged, when
// the second exercise took it up; each exercise names its row with its own test id. Taking the
// slack lets the word be set large; the row's own size never depends on whether a word is showing,
// so nothing jumps as a word appears and fades.
//
// Purely presentational: the caller works out which word is pulsing and how far along its pulse is
// (see visibleCue in learn_cue.ts) and hands it over, so a story or a test can render any instant.
// Nothing is rendered inside the row when `cue` is undefined — between pulses the row is empty.
// `large` sets the word bigger, for a screen with more slack to give it: Sing Rhythm's, where the
// tap button is gone (issue #3723).
export const CueRow = ({ cue, testId, large = false }: { cue?: VisibleCue; testId: string; large?: boolean }) => (
  <Row data-testid={testId} data-large={large ? 'true' : undefined} $large={large}>
    {cue && (
      <Word
        $large={large}
        data-word={cue.word}
        data-opacity={cue.opacity.toFixed(2)}
        $color={cueColorCss[cue.word]}
        style={{ opacity: cue.opacity, transform: `scale(${cue.scale})` }}
      >
        {cueText[cue.word]}
      </Word>
    )}
  </Row>
);

// How short the cue row may ever be: enough for the word at its own size, whatever slack the
// screen has to give it. The wrapper that hands it that slack (CueSlack, below) takes its own
// minimum from this one, through the content-based minimum of a flex item, so on a short screen the
// body scrolls rather than the row collapsing and the word painting over what is under it (issue
// #3559).
export const cueRowMinHeightPx = 80;
export const cueRowShortMinHeightPx = 64;
export const cueRowLargeMinHeightPx = 124;
export const cueRowLargeShortMinHeightPx = 96;

// Takes a screen's slack (in place of Slack) and hands it to the cue row inside it, so the cue word
// can fill it. Its `flex-basis: 0` is what it grows from, and its minimum is the one flexbox works
// out from its content - the cue row's own min-height, 80 px or 64 px on a short screen - so the row
// can never be squeezed past that and overflow, painting Ready · Set · Go! over what is under it
// (issue #3559). Nothing here mirrors those numbers: `min-height: 0` would be the thing that threw
// them away. Rhythm Echo adds its own margin above it (rhythm_screen_parts.tsx).
export const CueSlack = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
`;

const Row = styled.div<{ $large: boolean }>`
  flex: 1 1 0;
  min-height: ${({ $large }) => ($large ? cueRowLargeMinHeightPx : cueRowMinHeightPx)}px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${shortScreen} {
    min-height: ${({ $large }) => ($large ? cueRowLargeShortMinHeightPx : cueRowShortMinHeightPx)}px;
  }
`;

const Word = styled.div<{ $color: string; $large: boolean }>`
  font-family: Jost-SemiBold, Arial, sans-serif; /* the app's bold face; a font-weight would only fake a heavier one */
  font-size: ${({ $large }) => ($large ? 104 : 72)}px;
  line-height: 1;
  color: ${({ $color }) => $color};
  will-change: transform, opacity;

  ${shortScreen} {
    font-size: ${({ $large }) => ($large ? 84 : 56)}px;
  }
`;
